import { faker } from "@faker-js/faker";
import { BlueText } from "components/common";
import { COLORS } from "constant";
import _, { size, get } from "lodash";
import { useState, useEffect } from "react";
import { Row, Col, Form, Button } from "react-bootstrap";
import { displaySensorType } from "utils/functions";
import { ColorPicker } from "../create";
import DashboardCreateSelectSensorModal from "components/dashboard/create/modal/SelectDevices";
import { NoInformationColumn } from "components/common/NoInformationColumn";
import deleteSvg from "assets/svg/delete.svg";
import {
    getConsumptionCLSFromMetricName,
    getSensorType,
} from "utils/sensorFunctions";
import { SensorType } from "constant/gateway";
import {
    ChartSource as ChartSourceEnum,
    ChartType as ChartTypeEnum,
} from "constant/chart";
import CloseIcon from "@mui/icons-material/Close";
import { WidgetChartTypeEnum } from "types";
import {
    getMeterChartVariantFromChartSource,
    getMeterWidgetTypeByVariant,
    getWidgetConfigByWidgetType,
    MeterConsumptionType,
} from "../utils/widgetConfig";
import { getMQTTQueryByDevice } from "../utils/widgetUtil";
import { useStore } from "./single";
import { GATEWAY_LABEL, LDSU_UUID_LABEL } from "constant/displayLabels";
import Avatar from "@mui/material/Avatar";
import usePanelConfiguration from "hooks/usePanelConfiguration";

type ConsumptionWidgetType =
    | WidgetChartTypeEnum.WATER_CONSUMPTION
    | WidgetChartTypeEnum.ENERGY_CONSUMPTION;

const consumptionChartTypeBySensorType = {
    [SensorType.WaterConsumption]: WidgetChartTypeEnum.WATER_CONSUMPTION,
    [SensorType.EnergyConsumption]: WidgetChartTypeEnum.ENERGY_CONSUMPTION,
};

const resolveMeterWidgetTypeFromPanel = (panel: any, attributes: any[]) => {
    const firstAttributeSensorType = attributes?.length
        ? getSensorType({
            CLS: getConsumptionCLSFromMetricName(attributes[0].time_series),
        })
        : undefined;

    const detectedConsumptionType: MeterConsumptionType =
        firstAttributeSensorType === SensorType.WaterConsumption
            ? "water"
            : "energy";
    const meterChartType: ChartTypeEnum.BAR | ChartTypeEnum.STACKED_BAR =
        panel.chart === ChartTypeEnum.STACKED_BAR
            ? ChartTypeEnum.STACKED_BAR
            : ChartTypeEnum.BAR;
    const meterSourceType:
        | ChartSourceEnum.SINGLE_SENSOR
        | ChartSourceEnum.MULTIPLE_SENSORS =
        panel.source === ChartSourceEnum.MULTIPLE_SENSORS
            ? ChartSourceEnum.MULTIPLE_SENSORS
            : ChartSourceEnum.SINGLE_SENSOR;
    const meterVariant = getMeterChartVariantFromChartSource(
        meterChartType,
        meterSourceType
    );
    return getMeterWidgetTypeByVariant(meterVariant, detectedConsumptionType);
};

function mapChartData(devices: any[]): any[] {
    return devices.map((device) => {
        const {
            gatewayId,
            gateway_id,
            ldsuUID,
            said,
            SAID,
            show_alert,
            time_series,
            unit,
            color,
            sensorType,
        } = device;

        return {
            gateway_id: gateway_id ?? gatewayId,
            ldsu_uuid: ldsuUID,
            said: said ?? SAID,
            show_alert,
            time_series: time_series ?? getMQTTQueryByDevice(device),
            unit,
            color,
            sensorType,
            name: sensorType,
            device_id: ldsuUID,
            ldsuUID,
        };
    });
}

const CombinedConsumtionChartUpdate = ({
    panel,
    devices,
    isMutipleEdit = false,
    onChange,
    onDelete,
}: {
    panel: any;
    devices: any;
    isMutipleEdit?: boolean;
    onChange: Function;
    onDelete?: Function;
}) => {
    const store = useStore();
    const { originPanel: { attributes: originAttributes }, originPanel } = store;
    const { attributes, uuid } = panel;
    const widgetType = resolveMeterWidgetTypeFromPanel(originPanel, originAttributes);
    const widgetConfig = getWidgetConfigByWidgetType(widgetType);

    const { defaultValue } = usePanelConfiguration({
        panelData: panel,
        deviceList: devices,
    });

    const [chartName, setChartName] = useState(panel.name);
    const [showSelectModal, setShowSelectModal] = useState(false);
    const [chartType, setChartType] = useState<ConsumptionWidgetType | "">("");
    const [selectedDevices, setSelectedDevices] = useState<{
        [key: string]: any;
    } | null>(null);
    const filterSensor = widgetConfig?.filter?.(widgetType);

    const consumptionType =
        chartType === WidgetChartTypeEnum.WATER_CONSUMPTION
            ? "Water"
            : "Energy";
    const title = chartType
        ? `Sensors for Combined - ${consumptionType} consumption chart`
        : "";

    const canRemove =
        (isMutipleEdit && size(selectedDevices) > 2) || !isMutipleEdit;

    useEffect(() => {
        if (typeof onChange !== "function" || !selectedDevices) {
            return;
        }

        const attributeList = mapChartData(Object.values(selectedDevices));

        onChange({
            chartName,
            data: attributeList,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedDevices, chartName]);

    useEffect(() => {
        if (!attributes?.length || !devices?.length) {
            return;
        }

        const attributeList = attributes.map((item: any) => {
            const sensorCls = getConsumptionCLSFromMetricName(
                item.time_series
            );

            const device = devices.find(
                (device: any) =>
                    device.deviceId === `${item.device_id}/${item.said}`
            );

            return {
                key: `${item?.device_id}/${item.said}`,
                ...device,
                ...item,
                device_id: `${item.device_id}/${item.said}`,
                color: item.color,
                CLS: sensorCls,
            };
        });

        setSelectedDevices(attributeList.pluck("device_id"));

        const sensorType = getSensorType({ CLS: attributeList[0].CLS });
        if (sensorType) {
            const widgetType = consumptionChartTypeBySensorType[
                sensorType
            ] as ConsumptionWidgetType;
            setChartType(widgetType);
        } else {
            setChartType("");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [defaultValue]);


    const onChangeDevice = (data: any) => {
        const devices = Object.values(data).map((item: any, index: number) => ({
            ...item,
            color: get(
                selectedDevices,
                `[${item.deviceId}].color`,
                COLORS[index] ||
                faker.color.rgb({
                    format: "hex",
                })
            ),
            alert: false,
            device_id: `${item.ldsuUID}/${item.said}`,
        }));
        setSelectedDevices(devices.pluck("device_id"));
    };

    const onRemove = (device: any) => {
        const devices = _.omit(selectedDevices, device.deviceId);
        setSelectedDevices(devices);
    };

    const onChangeColor = (device: any, color: any) => {
        const devices = _.set(
            { ...(selectedDevices) },
            `${device.deviceId}.color`,
            color
        );

        setSelectedDevices(devices);
    };

    const onChangeChartName = (name: string) => {
        setChartName(name);
        onChange({
            chartName: name,
        });
    };

    return (
        <div className="multiple-chart-block form-box mb-3">
            <Row>
                <Col xs={12} sm="11">
                    <Form.Group>
                        <Form.Label>Widget Name</Form.Label>
                        <Form.Control
                            name="name"
                            type="text"
                            value={chartName}
                            placeholder=""
                            onChange={(event) =>
                                onChangeChartName(event.target.value)
                            }
                        />
                    </Form.Group>

                    <p>{title}</p>
                    <div className="my-2">
                        <Button
                            className="mr-3"
                            variant="primary"
                            onClick={() => setShowSelectModal(true)}
                        >
                            Select Sensors
                        </Button>
                        <BlueText>
                            You can add max {widgetConfig?.maxItems ?? 4}{" "}
                            sensors
                        </BlueText>
                    </div>
                    {size(selectedDevices) === 1 && (
                        <span className="text-danger">
                            Please select at least two sensors to create a
                            combined consumption chart.
                        </span>
                    )}

                    <DashboardCreateSelectSensorModal
                        show={showSelectModal}
                        onHide={() => setShowSelectModal(false)}
                        values={selectedDevices}
                        onSave={(event, data) => onChangeDevice(data)}
                        deviceType="Sensor"
                        filter={filterSensor}
                        maxDevices={widgetConfig?.maxItems ?? 4}
                    />
                </Col>
                {isMutipleEdit && (
                    <Col
                        sm={1}
                        className="d-flex justify-content-end"
                    >
                        <Avatar
                            alt="delete"
                            src={deleteSvg}
                            onClick={() => onDelete?.(uuid)}
                            onKeyDown={() => onDelete?.(uuid)}
                            sx={{
                                width: 24,
                                height: 24,
                                cursor: "pointer",
                            }}
                        />
                    </Col>
                )}
            </Row>
            <Row className="mt-4 cstm-table ">
                <Col sm={12}>
                    {!!Object.values(selectedDevices ?? {}).length && (
                        <div className="table-head">
                            <Row className="no-checkbox">
                                <Col md={2}>Sensor</Col>
                                <Col md={2}>Type</Col>
                                <Col md={3} sm={2}>
                                    {GATEWAY_LABEL}
                                </Col>
                                <Col md={3}>{LDSU_UUID_LABEL}</Col>
                                <Col lg={1} sm={2}>
                                    Color
                                </Col>
                                <Col md={1}></Col>
                            </Row>
                        </div>
                    )}
                    {Object.values(selectedDevices ?? {}).map(
                        (device, index) => (
                            <div
                                key={device.deviceId}
                                id={device.deviceId}
                                className="table-row"
                            >
                                <Row className="no-checkbox">
                                    <Col
                                        className="h-auto"
                                        md={device.sensorName ? 2 : 11}
                                    >
                                        {device.sensorName}
                                        {!device.sensorName && (
                                            <NoInformationColumn />
                                        )}
                                    </Col>
                                    {device.sensorName && (
                                        <>
                                            <Col className="h-auto" md={2}>
                                                {displaySensorType(device)}
                                            </Col>
                                            <Col
                                                className="h-auto"
                                                lg={3}
                                                sm={2}
                                            >
                                                {device.gatewayName}
                                            </Col>
                                            <Col className="h-auto" md={3}>
                                                {device.ldsuUID}
                                            </Col>
                                            <Col
                                                className="h-auto"
                                                sm={2}
                                                lg={1}
                                            >
                                                <ColorPicker
                                                    defaultValue={get(
                                                        selectedDevices,
                                                        `${device.deviceId}.color`,
                                                        COLORS[index]
                                                    )}
                                                    onChange={(event, color) =>
                                                        onChangeColor(
                                                            device,
                                                            color
                                                        )
                                                    }
                                                />
                                            </Col>
                                        </>
                                    )}
                                    {canRemove && (
                                        <Col
                                            md={1}
                                            className="d-flex justify-content-end h-auto"
                                        >
                                            <CloseIcon
                                                className="cursor-pointer mr-2 text-primary-red-1"
                                                onClick={() => onRemove(device)}
                                                onKeyDown={() =>
                                                    onRemove(device)
                                                }
                                            />
                                        </Col>
                                    )}
                                </Row>
                            </div>
                        )
                    )}
                </Col>
            </Row>
        </div>
    );
};

export default CombinedConsumtionChartUpdate;
